import React, { MouseEvent } from 'react'
import { ChecklistItem, Goal } from 'Generated/graphql-types'
import styled from '@emotion/styled'
import { useQuery } from '@apollo/client'
import { GOAL_UPDATES } from '../../../../../Apollo/Queries/GoalUpdates'
import { useAppState } from '../../../../../Context/AppContext/AppContext'
import { UpdatesTab } from '../../GoalPage/Updates/UpdatesTab'
import {
  useAwsFlagActive,
  showRecurringTasks,
} from '../../../../../Modules/FeatureFlag'
import { TaskListWithTabs } from '../../GoalPage/Tasks/TaskListV2/TasklistWithTabs'
import TaskList from '../../../Dashboard/NonprofitPlatformDashboard/TaskList/TaskList'
import { GoalProgessUpdatesTab } from './GoalProgessUpdatesTab'
import { GoalAttachments } from './GoalAttachments'

type GoalTabsProps = {
  goal: Goal
  tab?: string
  isGoalSharedWithFunder: boolean
  externalSnapshotText?: string | null
  onExternalActionProcessed?: () => void
}

export const GoalTabs = ({
  goal,
  tab,
  isGoalSharedWithFunder,
  externalSnapshotText,
  onExternalActionProcessed,
}: GoalTabsProps) => {
  const {
    nonprofitOrgConfig: { orgId: nonprofitOrgId },
  } = useAppState()
  const shouldShowRecurringTasks = useAwsFlagActive(showRecurringTasks)

  const tasks = goal?.ChecklistItem as unknown as ChecklistItem[]

  const { data: { goalUpdates = [] } = {}, loading: goalUpdatesLoading } =
    useQuery(GOAL_UPDATES, {
      variables: {
        nonprofitOrgId,
        goalId: goal.id,
      },
    })

  const funderSharedGoal = goal.FunderOrgToSharedGoal.filter(
    (sharedGoal) => sharedGoal.goalId === goal.id
  )[0]

  const attachmentsSection = <GoalAttachments goal={goal} />

  const progressUpdatesSection = (
    <GoalProgessUpdatesTab
      goal={goal}
      isGoalSharedWithFunder={isGoalSharedWithFunder}
    />
  )

  const snapshotSection = (
    <UpdatesTab
      updates={goalUpdates.nodes}
      updatesLoading={goalUpdatesLoading}
      goalId={goal.id}
      simple
      externalSnapshotText={externalSnapshotText}
      onExternalActionProcessed={onExternalActionProcessed}
    />
  )

  const taskSection = shouldShowRecurringTasks ? (
    <TaskListWithTabs goal={goal} />
  ) : (
    <TaskList
      simple
      items={tasks}
      eventProps={{
        goalId: goal.id,
        goalType: goal.type,
        templateLibraryId: goal.templateLibraryId ?? null,
        isGoalSharedWithFunder: !!funderSharedGoal,
      }}
    />
  )

  const prevent = (event: MouseEvent) => {
    event.stopPropagation()
  }

  return (
    <Container onClick={prevent}>
      <div className='tab-container'>
        {tab === 'attachments' && attachmentsSection}
        {tab === 'progress' && progressUpdatesSection}
        {tab === 'snapshots' && snapshotSection}
        {tab === 'tasks' && taskSection}
      </div>
    </Container>
  )
}

const Container = styled.div`
  max-height: 468px;
  margin-bottom: 32px;
  overflow: hidden;
  height: auto;
  padding: 32px 32px 0 32px;
  text-align: left;
  width: 100%;
  & .tab-container {
    .scroll {
      width: 100%;
      max-height: 420px;
      overflow-y: scroll;
    }
  }
`
